/**
 * The client-side half of the API's InLocaleScript rule.
 *
 * The server is the authority — this exists so somebody typing English into the
 * Arabic box is told at the keystroke rather than at the save. The two must
 * agree, so the tests here are the same intersection the PHP rule uses:
 *
 *   - a character that is both a letter and Latin script
 *   - a character that is both a letter and Arabic script
 *
 * Intersecting with \p{L} is what lets digits through in either shape:
 * Arabic-Indic digits (٠-٩) carry Script=Arabic but are not letters, so
 * "Package ٢" passes an English field exactly as "باقة 2" passes an Arabic one.
 * Punctuation, symbols and emoji are Common script and are never matched.
 */

const LATIN_LETTER = /(?=\p{L})\p{Script=Latin}/u;
const ARABIC_LETTER = /(?=\p{L})\p{Script=Arabic}/u;

/**
 * Is this value written in the wrong script for its locale?
 *
 * Empty is always fine: whether a field may be blank is its own required rule's
 * business, and answering it here would put two errors under one input.
 *
 * @param {"en"|"ar"} locale
 * @param {string} value
 * @returns {boolean}
 */
export const isWrongScript = (locale, value) => {
  if (typeof value !== "string" || value.trim() === "") return false;

  return locale === "ar" ? LATIN_LETTER.test(value) : ARABIC_LETTER.test(value);
};

/** The i18n key for the message, or null when the value is fine. */
export const scriptErrorKey = (locale, value) =>
  isWrongScript(locale, value) ? `validation.script.${locale}` : null;
